import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Box, Grid } from '@mui/material';

import { getInvestorsCount, shouldHideInvestorCount } from '@arrived/common';
import { AssetType, Offering, Property } from '@arrived/models';
import { useCanUserTransactEarlyAccess } from '@arrived/queries';

import { Money } from '../../../Money';
import { RentalStatusQuickLookItem } from '../PropertyDetailsRentInfo/PropertyDetailsRentalStatusQuickLookItem';
import { PropertyDetailsQuickLookItem } from './PropertyDetailsQuickLookItem';

import {
  ArrivedTooltip,
  InvestorsTooltip,
  MonthlyRentTooltip,
  PurchasePriceTooltip,
  FlexFinancingTooltip,
} from '../../../tooltips';
import { isRentInfoSectionEnabled } from '../PropertyDetailsRentInfo/utils';

interface PropertyDetailsQuickLookProps {
  offering: Offering;
}

export const PropertyDetailsQuickLook = ({ offering }: PropertyDetailsQuickLookProps) => {
  const { assetType, debtPercent, status, isFund, properties, totalPurchasePrice, hasFlexFinancing } = offering;
  const { leaseStatus, rent } = properties[0] || ({} as Property);
  const canUserTransactEarlyAccess = useCanUserTransactEarlyAccess();

  return (
    <Grid container spacing={3}>
      {!shouldHideInvestorCount(status, canUserTransactEarlyAccess) && (
        <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
          <PropertyDetailsQuickLookItem label={<InvestorsTooltip />} value={getInvestorsCount(offering)} />
        </Grid>
      )}
      {!isFund && (
        <>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <PropertyDetailsQuickLookItem
              label={<PurchasePriceTooltip />}
              value={<Money value={totalPurchasePrice} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
            <PropertyDetailsQuickLookItem
              label={
                <ArrivedTooltip title={<FormattedMessage id="property-leverage" defaultMessage="Property Leverage" />}>
                  <FormattedMessage
                    id="leverage-description"
                    defaultMessage={
                      'Leverage uses borrowed capital or debt to increase the potential return of an investment. Leverage works to your advantage when real estate values rise, but it can also lead to losses if values decline.'
                    }
                  />
                </ArrivedTooltip>
              }
              value={
                <Box display="flex" flex={1} flexDirection="column">
                  <FormattedNumber value={debtPercent / 100} style="percent" maximumFractionDigits={2} />
                  {hasFlexFinancing && <FlexFinancingTooltip />}
                </Box>
              }
            />
          </Grid>
          {assetType === AssetType.LTR && (
            <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
              <PropertyDetailsQuickLookItem label={<MonthlyRentTooltip />} value={<Money value={rent || 0} />} />
            </Grid>
          )}
          {!isRentInfoSectionEnabled(offering) && leaseStatus && (
            <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
              <RentalStatusQuickLookItem leaseStatus={leaseStatus} />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
